/**
 * useMentions Hook
 *
 * Provides identity search functionality for @mentions in chat.
 * Includes debouncing, caching, and error handling.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "@webuild-ai/react-auth";

import { useMentionStore, IdentitySearchResult } from "@/stores/MentionStore";
import { siteConfig } from "@/config/site";
import { logger } from "@/telemetry";

import { useDebouncedValue } from "./useDebouncedValue";

interface UseMentionsOptions {
  /** Debounce delay in milliseconds (default: 200) */
  debounceMs?: number;
  /** Search mode: 'name' (default) or 'email' (search by email) */
  searchMode?: "name" | "email";
}

interface UseMentionsReturn {
  /** Current search query */
  query: string;
  /** Set the search query */
  setQuery: (query: string) => void;
  /** Search results */
  results: IdentitySearchResult[];
  /** Loading state */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Manually trigger a search */
  search: (query: string) => Promise<void>;
  /** Clear results */
  clearResults: () => void;
}

/**
 * Hook for searching identities to mention.
 *
 * @param options - Configuration options
 * @returns Search state and methods
 *
 * @example
 * const { query, setQuery, results, loading } = useMentions();
 *
 * // Update query as user types
 * setQuery(inputValue);
 *
 * // Results are automatically debounced and cached
 * results.map(identity => <IdentityItem key={identity.id} {...identity} />)
 */
export function useMentions(options: UseMentionsOptions = {}): UseMentionsReturn {
  const { debounceMs = 200, searchMode = "name" } = options;
  const { getAccessTokenSilently } = useAuth();
  const { getCache, setCache, recentIdentities } = useMentionStore();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<IdentitySearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedQuery = useDebouncedValue(query, debounceMs);
  const abortControllerRef = useRef<AbortController | null>(null);

  /**
   * Perform the actual API search using direct fetch.
   * This bypasses the generated client which may not have the mentions endpoints yet.
   */
  const search = useCallback(
    async (searchQuery: string) => {
      const trimmedQuery = searchQuery.trim();

      // Empty query - show recent identities instead
      if (!trimmedQuery) {
        setResults(recentIdentities);
        setLoading(false);
        setError(null);

        return;
      }

      // Check cache first
      const cached = getCache(trimmedQuery);

      if (cached) {
        setResults(cached);
        setLoading(false);
        setError(null);

        return;
      }

      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setLoading(true);
      setError(null);

      try {
        // Get auth token
        if (!getAccessTokenSilently) {
          throw new Error("Auth not initialized");
        }
        const token = await getAccessTokenSilently();

        if (!token) {
          throw new Error("Not authenticated");
        }

        // Build URL - admin API routes are prefixed with /admin/v1
        const baseUrl = siteConfig().adminAPIURL.replace(/\/$/, "");
        const searchModeParam = searchMode === "email" ? "&search_mode=email" : "";
        const url = `${baseUrl}/admin/v1/mentions/search?query=${encodeURIComponent(trimmedQuery)}&limit=10${searchModeParam}`;

        // Make direct fetch request
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`Search failed: ${response.status}`);
        }

        const data = await response.json();

        // Map response to our interface
        const mappedResults: IdentitySearchResult[] = (data || []).map((r: any) => ({
          id: r.id,
          displayName: r.display_name || r.displayName,
          canonicalEmail: r.canonical_email || r.canonicalEmail,
          avatarUrl: r.avatar_url || r.avatarUrl,
          availableServices: r.available_services || r.availableServices || [],
          matchScore: r.match_score || r.matchScore,
          serviceUserIds: r.service_user_ids || r.serviceUserIds,
        }));

        setResults(mappedResults);
        setCache(trimmedQuery, mappedResults);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") {
          // Request was cancelled, ignore
          return;
        }
        logger.error("Mention search error", { error: String(err) });
        setError(err instanceof Error ? err.message : "Failed to search identities");
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [getAccessTokenSilently, getCache, setCache, recentIdentities, searchMode],
  );

  /**
   * Clear results and error.
   */
  const clearResults = useCallback(() => {
    setResults([]);
    setError(null);
  }, []);

  // Trigger search when debounced query changes
  useEffect(() => {
    search(debouncedQuery);
  }, [debouncedQuery, search]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    query,
    setQuery,
    results,
    loading,
    error,
    search,
    clearResults,
  };
}

/**
 * Add an identity to the recent list.
 * Call this when a user selects an identity from the dropdown.
 */
export function useAddRecentIdentity() {
  const { addRecentIdentity } = useMentionStore();

  return addRecentIdentity;
}

/**
 * Get recent identities for quick access.
 */
export function useRecentIdentities() {
  const { recentIdentities } = useMentionStore();

  return recentIdentities;
}
